import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CreateConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  User, Phone, CreditCard, Building2,
  Loader2, Eye, EyeOff, UserPlus, Shield, CheckCircle,
} from 'lucide-react'
import { getUserService } from '@/domain/user/user.service'
import { useAuth } from '@/components/auth/auth-provider'
import { cn } from '@/lib/utils'
import { BankLogo } from '@/components/ui/bank-logo'
import { getActiveBanks, findBank, type CurrencyCode } from '@/data/banks'
import { useCurrency } from '@/stores/currency-store'

interface AddMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function AddMemberDialog({ open, onOpenChange, onSuccess }: AddMemberDialogProps) {
  const { sessionToken } = useAuth()
  const { currency } = useCurrency()
  
  // ดึง bank list ตาม currency ที่เลือก
  const BANK_LIST = getActiveBanks(currency.code as CurrencyCode).map(b => ({
    id: b.id,
    name: b.name,
    code: b.code.toUpperCase(),
  }))
  
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    lineId: '',
    bankName: '',
    bankAccount: '',
    password: '',
    confirmPassword: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      lineId: '',
      bankName: '',
      bankAccount: '',
      password: '',
      confirmPassword: '',
    })
    setErrors({})
    setError(null)
    setSuccess(false)
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.firstName.trim()) newErrors.firstName = 'กรุณากรอกชื่อ'
    if (!formData.lastName.trim()) newErrors.lastName = 'กรุณากรอกนามสกุล'
    if (!formData.phone.trim()) newErrors.phone = 'กรุณากรอกเบอร์โทรศัพท์'
    else if (!/^0\d{9}$/.test(formData.phone)) newErrors.phone = 'เบอร์โทรศัพท์ไม่ถูกต้อง (0xxxxxxxxx)'
    if (!formData.bankName) newErrors.bankName = 'กรุณาเลือกธนาคาร'
    if (!formData.bankAccount.trim()) newErrors.bankAccount = 'กรุณากรอกเลขบัญชี'
    else if (!/^\d{10,15}$/.test(formData.bankAccount.replace(/-/g, ''))) newErrors.bankAccount = 'เลขบัญชีไม่ถูกต้อง'
    if (!formData.password) newErrors.password = 'กรุณากรอกรหัสผ่าน'
    else if (formData.password.length < 6) newErrors.password = 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'รหัสผ่านไม่ตรงกัน'
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'อีเมลไม่ถูกต้อง'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // เปิด confirm dialog ก่อนสร้าง
  const handleCreateClick = () => {
    if (!validate()) return
    setShowConfirm(true)
  }

  // สร้างสมาชิกจริงหลังยืนยัน
  const handleConfirmCreate = async () => {
    if (!sessionToken) {
      setError('กรุณาเข้าสู่ระบบ')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // ✅ Auto-calculate bank_type based on selected bank
      // bank_type = 2 if TrueWallet, otherwise 1 (regular bank)
      const selectedBank = findBank(formData.bankName)
      const bankType = selectedBank?.id === '19' || selectedBank?.code === 'truewallet' ? 2 : 1

      const userService = await getUserService()
      await userService.createMember(sessionToken, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email || undefined,
        lineId: formData.lineId || undefined,
        bankName: formData.bankName,
        bankAccount: formData.bankAccount.replace(/-/g, ''),
        bankType, // ✅ Auto-set bank_type (1=Bank, 2=TrueWallet)
        password: formData.password,
      })

      setShowConfirm(false)
      setSuccess(true)
      setTimeout(() => {
        onSuccess?.()
        onOpenChange(false)
        resetForm()
      }, 1500)

    } catch (err: any) {
      console.error('Error creating member:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการสร้างสมาชิก')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            เพิ่มสมาชิกใหม่
          </DialogTitle>
          <DialogDescription>
            กรอกข้อมูลสมาชิกใหม่ด้านล่าง
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-emerald-600 mb-2">สร้างสมาชิกสำเร็จ!</h3>
            <p className="text-muted-foreground">กำลังปิดหน้าต่าง...</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Personal Info */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-blue-500" />
                ข้อมูลส่วนตัว
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className="text-xs">ชื่อ <span className="text-red-500">*</span></Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="ชื่อ"
                    className={cn(errors.firstName && 'border-red-500')}
                  />
                  {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className="text-xs">นามสกุล <span className="text-red-500">*</span></Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="นามสกุล"
                    className={cn(errors.lastName && 'border-red-500')}
                  />
                  {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs">เบอร์โทรศัพท์ (Username) <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="0xxxxxxxxx"
                    className={cn("pl-9", errors.phone && 'border-red-500')}
                  />
                </div>
                {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
              </div>

            </div>

            {/* Bank Info */}
            <div className="space-y-3 pt-2 border-t">
              <h4 className="font-medium flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-emerald-500" />
                ข้อมูลธนาคาร
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">ธนาคาร <span className="text-red-500">*</span></Label>
                  <Select value={formData.bankName} onValueChange={(value) => setFormData({ ...formData, bankName: value })}>
                    <SelectTrigger className={cn(errors.bankName && 'border-red-500')}>
                      <SelectValue placeholder="เลือกธนาคาร">
                        {formData.bankName && (
                          <div className="flex items-center gap-2">
                            <BankLogo bankValue={formData.bankName} size="xs" />
                            <span>{findBank(formData.bankName)?.name}</span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {BANK_LIST.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          <div className="flex items-center gap-2">
                            <BankLogo bankValue={bank.id} size="xs" />
                            <span>{bank.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.bankName && <p className="text-xs text-red-500">{errors.bankName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bankAccount" className="text-xs">เลขบัญชี <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="bankAccount"
                      value={formData.bankAccount}
                      onChange={(e) => setFormData({ ...formData, bankAccount: e.target.value.replace(/[^\d-]/g, '') })}
                      placeholder="xxx-x-xxxxx-x"
                      className={cn("pl-9 font-mono", errors.bankAccount && 'border-red-500')}
                    />
                  </div>
                  {errors.bankAccount && <p className="text-xs text-red-500">{errors.bankAccount}</p>}
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-3 pt-2 border-t">
              <h4 className="font-medium flex items-center gap-2 text-sm">
                <Shield className="w-4 h-4 text-orange-500" />
                รหัสผ่าน
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs">รหัสผ่าน <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="อย่างน้อย 6 ตัวอักษร"
                      className={cn(errors.password && 'border-red-500')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-xs">ยืนยันรหัสผ่าน <span className="text-red-500">*</span></Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="กรอกรหัสผ่านอีกครั้ง"
                    className={cn(errors.confirmPassword && 'border-red-500')}
                  />
                  {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={() => handleClose(false)} disabled={isLoading}>
                ยกเลิก
              </Button>
              <Button className="flex-1" onClick={handleCreateClick} disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                สร้างสมาชิก
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Confirm Create Dialog */}
      <CreateConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleConfirmCreate}
        isLoading={isLoading}
        itemName="สมาชิก"
        summary={
          <div className="space-y-2 text-sm">
            <p className="font-medium text-foreground">ข้อมูลสมาชิกใหม่:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
              <span>ชื่อ-สกุล:</span>
              <span className="text-foreground">{formData.firstName} {formData.lastName}</span>
              <span>เบอร์โทร:</span>
              <span className="text-foreground">{formData.phone}</span>
              <span>ธนาคาร:</span>
              <span className="text-foreground">{BANK_LIST.find(b => b.id === formData.bankName)?.name || '-'}</span>
              <span>เลขบัญชี:</span>
              <span className="text-foreground">{formData.bankAccount}</span>
            </div>
          </div>
        }
      />
    </Dialog>
  )
}

